import {
    Box,
    Flex,
    Heading,
    Text,
    useColorModeValue,
    VStack,
    Center
} from "@chakra-ui/react";
import { motion } from "framer-motion";

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionHeading = motion(Heading);
const MotionText = motion(Text);

// Animation configurations
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.25,
            delayChildren: 0.3
        }
    }
};

const wordVariants = {
    hidden: { y: 80, opacity: 0, scale: 0.85, rotateX: -45 },
    visible: {
        y: 0,
        opacity: 1,
        scale: 1,
        rotateX: 0,
        transition: {
            type: "spring",
            damping: 15,
            stiffness: 120
        }
    }
};

const subtitleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.8,
            ease: "easeOut"
        }
    }
};

const pulseVariants = {
    animate: {
        scale: [1, 1.08, 0.94, 1.04, 1],
        opacity: [0.15, 0.25, 0.12, 0.22, 0.15],
        transition: {
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
        }
    }
};

export default function EnterprisePage() {
    const bgGradient = useColorModeValue(
        "radial-gradient(circle at center, #f7fafc, #edf2f7)",
        "radial-gradient(circle at center, #0f172a, #020617)"
    );
    const textShadowVal = useColorModeValue(
        "0 4px 20px rgba(49, 151, 149, 0.15)",
        "0 4px 30px rgba(49, 151, 149, 0.4)"
    );
    const subtitleColor = useColorModeValue("gray.600", "teal.200");

    return (
        <Center
            w="100%"
            minH="calc(100vh - 72px)"
            bgGradient={bgGradient}
            position="relative"
            overflow="hidden"
            style={{ perspective: "1000px" }}
        >
            {/* Ambient Glowing Background Orbs */}
            <MotionBox
                variants={pulseVariants}
                animate="animate"
                position="absolute"
                w="600px"
                h="600px"
                borderRadius="full"
                bgGradient="radial(teal.500, transparent 65%)"
                filter="blur(80px)"
                zIndex={0}
            />
            <MotionBox
                animate={{
                    x: [0, -50, 40, 0],
                    y: [0, 60, -30, 0],
                    scale: [1, 0.9, 1.1, 1],
                    opacity: [0.08, 0.18, 0.08]
                }}
                transition={{
                    duration: 12,
                    repeat: Infinity,
                    ease: "easeInOut"
                }}
                position="absolute"
                w="500px"
                h="500px"
                borderRadius="full"
                bgGradient="radial(blue.500, transparent 65%)"
                filter="blur(90px)"
                zIndex={0}
            />

            {/* Kinetic Typography Content */}
            <MotionFlex
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                direction="column"
                align="center"
                zIndex={1}
                px={6}
                textAlign="center"
                maxW="5xl"
            >
                <VStack spacing={2} style={{ transformStyle: "preserve-3d" }}>
                    
                    {/* Big Brand Title: XNav */}
                    <MotionBox variants={wordVariants} style={{ transformStyle: "preserve-3d" }}>
                        <Heading
                            fontSize={{ base: "6xl", md: "8xl", lg: "9xl" }}
                            fontWeight="950"
                            letterSpacing="-4px"
                            bgGradient="linear(to-r, teal.300, cyan.400, blue.500)"
                            bgClip="text"
                            lineHeight="0.9"
                            textShadow={textShadowVal}
                            filter="drop-shadow(0px 15px 25px rgba(0, 0, 0, 0.15))"
                            _dark={{
                                filter: "drop-shadow(0px 15px 45px rgba(49, 151, 149, 0.25))"
                            }}
                        >
                            XNav
                        </Heading>
                    </MotionBox>

                    {/* Sub-Title: Enterprise */}
                    <MotionBox variants={wordVariants} style={{ transformStyle: "preserve-3d" }}>
                        <Heading
                            fontSize={{ base: "4xl", md: "6xl", lg: "7xl" }}
                            fontWeight="900"
                            letterSpacing="-2px"
                            bgGradient="linear(to-r, gray.100, gray.400)"
                            _light={{
                                bgGradient: "linear(to-r, gray.800, gray.600)"
                            }}
                            bgClip="text"
                            lineHeight="1"
                            mb={4}
                        >
                            ENTERPRISE
                        </Heading>
                    </MotionBox>

                    {/* Fancy Accent Bar */}
                    <MotionBox
                        variants={wordVariants}
                        w="80px"
                        h="4px"
                        borderRadius="full"
                        bgGradient="linear(to-r, teal.300, blue.400)"
                        mb={6}
                        boxShadow="0 0 12px #319795"
                    />

                    {/* Coming Soon statement */}
                    <MotionText
                        variants={subtitleVariants}
                        fontSize={{ base: "lg", md: "2xl", lg: "3xl" }}
                        fontWeight="semibold"
                        color={subtitleColor}
                        letterSpacing="-0.5px"
                        maxW="3xl"
                        px={4}
                    >
                        Coming Soon for Universities, Organizations, & Enterprises
                    </MotionText>

                    {/* Interactive Ambient Ring Indicator */}
                    <MotionBox
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: [0.3, 0.6, 0.3], scale: [1, 1.05, 1] }}
                        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                        w="160px"
                        h="40px"
                        mt={12}
                        borderRadius="full"
                        border="1px solid"
                        borderColor="teal.400/20"
                        bg="teal.500/5"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Text fontSize="10px" fontWeight="bold" letterSpacing="2px" color="teal.300" textTransform="uppercase">
                            Under Development
                        </Text>
                    </MotionBox>
                </VStack>
            </MotionFlex>
        </Center>
    );
}
